import React, { useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { startPayfastCheckout } from "@/lib/payments";
import { Avatar, Button, Loading, EmptyState } from "@/components/brand";
import { PageHeader } from "@/components/PageHeader";
import { CheckoutSheet } from "@/components/CheckoutSheet";
import { formatR, formatTime } from "@/lib/format";
import { cn } from "@/lib/utils";

export default function BookPrivate() {
  const { trainerId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const days = useMemo(() => Array.from({ length: 10 }, (_, i) => dayjs().add(i, "day")), []);
  const [date, setDate] = useState(days[0].format("YYYY-MM-DD"));
  const [duration, setDuration] = useState(sp.get("duration") ? Number(sp.get("duration")) : null);
  const [slot, setSlot] = useState(null);
  const [checkout, setCheckout] = useState(false);

  const t = useQuery({ queryKey: ["trainer", trainerId], queryFn: () => api(`/trainers/${trainerId}`) });
  const dur = duration ?? t.data?.private_pricing?.[0]?.duration_min ?? 60;
  const slots = useQuery({ queryKey: ["slots", trainerId, date, dur], queryFn: () => api(`/trainers/${trainerId}/slots?date=${date}&duration_min=${dur}`), enabled: !!t.data });
  const price = t.data?.private_pricing?.find((p) => p.duration_min === dur)?.price ?? 0;
  const badges = useQuery({ queryKey: ["me", "badges"], queryFn: () => api("/me/badges") });

  const book = useMutation({
    mutationFn: async ({ method, usePoints }) => {
      const b = await api("/bookings/private", { method: "POST", body: { trainer_id: trainerId, start_time: slot, duration_min: dur, payment_method: method, use_points: usePoints } });
      if (b.status === "pending_payment") { await startPayfastCheckout(b.id, `/booking/${b.id}?pf=1`); return null; }
      return b;
    },
    onSuccess: (b) => {
      setCheckout(false);
      if (!b) return;
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["slots"] });
      navigate(`/booking/${b.id}?confirmed=1`);
    },
    onError: (e) => { setCheckout(false); toast.error(e.message); },
  });

  if (t.isLoading) return <Loading />;
  if (!t.data) return <><PageHeader /><EmptyState title="Trainer not found" /></>;

  return (
    <div data-testid="book-private-screen" className="max-w-3xl">
      <PageHeader title="Private Session" onBack={() => navigate(-1)} />
      <div className="flex items-center gap-3 p-3 rounded-xl2 bg-brand-soft">
        <Avatar uri={t.data.avatar} size={52} name={t.data.name} />
        <div><div className="font-display text-lg text-ink">{t.data.name}</div><div className="font-body text-xs text-mutedgrey">{t.data.headline}</div></div>
      </div>

      <h3 className="font-display text-lg text-ink mt-6 mb-2">1. Choose duration</h3>
      <div className="flex gap-3">
        {(t.data.private_pricing || []).map((p) => (
          <button key={p.duration_min} data-testid={`duration-${p.duration_min}`} onClick={() => { setDuration(p.duration_min); setSlot(null); }}
            className={cn("flex-1 p-4 rounded-md border text-center", dur === p.duration_min ? "bg-brand border-brand text-white" : "border-line")}>
            <div className={cn("font-body font-semibold text-[13px]", dur === p.duration_min ? "text-white" : "text-ink")}>{p.duration_min} min</div>
            <div className={cn("font-display text-lg", dur === p.duration_min ? "text-white" : "text-brand")}>{formatR(p.price)}</div>
          </button>
        ))}
      </div>

      <h3 className="font-display text-lg text-ink mt-6 mb-2">2. Pick a day</h3>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {days.map((d) => {
          const key = d.format("YYYY-MM-DD");
          const active = key === date;
          return (
            <button key={key} data-testid={`day-${key}`} onClick={() => { setDate(key); setSlot(null); }}
              className={cn("shrink-0 w-14 h-[68px] rounded-md border flex flex-col items-center justify-center", active ? "bg-ink border-ink text-white" : "border-line")}>
              <span className={cn("font-body text-xs", active ? "text-white/70" : "text-mutedgrey")}>{d.format("ddd")}</span>
              <span className={cn("font-display text-xl", active ? "text-white" : "text-ink")}>{d.format("D")}</span>
            </button>
          );
        })}
      </div>

      <h3 className="font-display text-lg text-ink mt-6 mb-2">3. Select a time <span className="font-body text-sm text-mutedgrey">(your local time)</span></h3>
      {slots.isLoading ? <Loading /> : (slots.data ?? []).length === 0 ? (
        <EmptyState title="Not available this day" subtitle="Try another day." />
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {(slots.data ?? []).map((s) => (
            <button key={s.start_time} data-testid={`slot-${formatTime(s.start_time).replace(":", "")}`} disabled={!s.available} onClick={() => setSlot(s.start_time)}
              className={cn("p-3 rounded-md border text-center", !s.available ? "bg-surface2 border-surface2 opacity-60" : slot === s.start_time ? "bg-brand border-brand text-white" : "border-line")}>
              <div className={cn("font-display text-base", slot === s.start_time ? "text-white" : !s.available ? "text-mutedgrey line-through" : "text-ink")}>{formatTime(s.start_time)}</div>
              <div className={cn("font-body text-[10px]", slot === s.start_time ? "text-white/80" : "text-mutedgrey")}>{s.available ? "Available" : "Booked"}</div>
            </button>
          ))}
        </div>
      )}

      <div className="sticky bottom-0 -mx-4 sm:-mx-6 mt-8 flex items-center gap-4 p-4 bg-white border-t border-divider">
        <div>
          <div className="font-display text-2xl text-brand">{formatR(price)}</div>
          <div className="font-body text-xs text-mutedgrey">{slot ? `${dayjs(slot).format("ddd D MMM")} · ${formatTime(slot)} · ${dur} min` : "Select a time slot"}</div>
        </div>
        <Button testID="private-continue-button" title="Continue to Pay" icon={ArrowRight} disabled={!slot} onClick={() => setCheckout(true)} className="flex-1" />
      </div>

      <CheckoutSheet open={checkout} onClose={() => setCheckout(false)} title={`${dur}-minute Private Training`} subtitle={`${t.data.name} · ${slot ? dayjs(slot).format("ddd D MMM, HH:mm") : ""}`}
        amount={price} loading={book.isPending} onPay={(m, p) => book.mutate({ method: m, usePoints: p })} points={badges.data?.points ?? 0} />
    </div>
  );
}
